import * as React from 'react';
import { withRouter, RouteComponentProps } from 'react-router-dom';

import withStyles, {
  WithStyles,
  StyleRulesCallback
} from '@material-ui/core/styles/withStyles';

import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';

import { DocumentGrid } from '../components';

import {
  FieldProvider,
  LayoutToolsProvider,
  LayoutTools,
  LayersProvider
} from '../providers';

import PrescriptionProvider, {
  PrescriptionParams,
  PrescriptionResult
} from '../providers/PrescriptionProvider';

import {
  Paper,
  Typography,
  Button,
  Snackbar,
  CircularProgress,
  Table,
  TableHead,
  TableCell,
  TableRow,
  TableBody,
  Input,
  Card,
  CardHeader,
  CardContent,
  TextField,
  MenuItem,
  IconButton
} from '@material-ui/core';

import BackIcon from '@material-ui/icons/ArrowBack';
import { Stepper, Step, StepLabel, StepContent } from '@material-ui/core';
import SidePanel from './SidePanel';
import { readLabelBand } from 'geotiff-utils';

const MODELS = [
  'John Deere',
  'DyE',
  'Trimble',
  'Precision planting',
  'Verion',
  'Agrotax',
  'Controlagro',
  'plantium',
  'Muller',
  'SHP generico'
];

const CHANNELS = [
  'PL/10 metros lineal',
  'pl/ha',
  'kg/ha semilla',
  'kg/ha fertilizante 1',
  'kg/ha fertilizante 2'
];

const DISTANCES = ['35', '38', '42', '52,5', '70', '105'];

interface Renders {
  layout: LayoutTools;
  loading: boolean;
  field: Field | null;
  farm: Farm | null;
  layers: ZoneAnalysisDocument[] | null;
  execute: (params: PrescriptionParams) => void;
  processing: boolean;
  isBlankSlate: boolean;
}

interface State {
  activeStep: number;
  zonesDoc: ZoneAnalysisDocument | null;
  monitorModel: string | undefined;
  machineryModel: string | undefined;
  observations: string | undefined;
  lineDistance: string | undefined;
  channel1Type: string | undefined;
  channel2Type: string | undefined;
  channel3Type: string | undefined;
  records: PrescriptionRecord[];
  saving: boolean;
  result: PrescriptionResult | null;
  formErrors: string[];
}

const styles: StyleRulesCallback = theme => ({
  root: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    height: '100vh'
  },
  stepper: {
    marginTop: theme.spacing.unit * 2
  },
  content: {
    padding: theme.spacing.unit * 3,
    backgroundColor: theme.palette.grey['100'],
    flex: 1,
    overflowY: 'scroll',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  contentCentered: {
    padding: theme.spacing.unit * 3,
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center'
  },
  card: {
    width: '100%',
    maxWidth: 900,
    overflow: 'visible',
    marginBottom: theme.spacing.unit * 3
  },
  form: {
    display: 'flex',
    flexWrap: 'wrap'
  },
  textField: {
    marginLeft: theme.spacing.unit,
    marginRight: theme.spacing.unit,
    width: 200
  },
  inputCell: {
    maxWidth: '50px'
  }
});

type Props = WithStyles & RouteComponentProps<any>;

function* yieldFormErrors(state: State): IterableIterator<string> {
  if (!state.machineryModel && !state.monitorModel) {
    yield 'Debe seleccionar un modelo de maquinaria o monitor';
  }

  if (!state.channel1Type && !state.channel2Type && !state.channel3Type) {
    yield 'Debe seleccionar al menos un canal';
  }

  if (state.channel1Type && !state.records.every(r => !!r.channel1 || r.channel1 === 0)) {
    yield 'Debe completar todos los valores del canal 1';
  }

  if (state.channel2Type && !state.records.every(r => !!r.channel2 || r.channel2 === 0)) {
    yield 'Debe completar todos los valores del canal 2';
  }

  if (state.channel3Type && !state.records.every(r => !!r.channel3 || r.channel3 === 0)) {
    yield 'Debe completar todos los valores del canal 3';
  }
}

class ZoneAnalysis extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      activeStep: 0,
      zonesDoc: null,
      saving: false,
      monitorModel: '',
      machineryModel: '',
      lineDistance: '',
      observations: '',
      channel1Type: '',
      channel2Type: '',
      channel3Type: '',
      records: [],
      result: null,
      formErrors: []
    };
  }

  handleSelectClick = async (renders: Renders) => {
    const { zonesDoc } = this.state;

    if (!zonesDoc) {
      return;
    }

    const response = await fetch(zonesDoc.rasterUrl);
    const buffer = await response.arrayBuffer();
    const band = await readLabelBand(buffer, 99);

    const records: PrescriptionRecord[] = band.labels.map(b => ({
      zoneId: b.value,
      channel1: null,
      channel2: null,
      channel3: null
    }));

    await this.setState({ activeStep: 1, records });
  };

  handleExecute = async (renders: Renders) => {
    if (!this.state.zonesDoc || !this.state.records) {
      return;
    }

    const formErrors = Array.from(yieldFormErrors(this.state));

    if (formErrors.length) {
      this.setState({ formErrors });
      return;
    }

    this.setState({ activeStep: 2 });

    renders.execute({
      documentId: this.state.zonesDoc.id,
      monitorModel: this.state.monitorModel || '',
      machineryModel: this.state.machineryModel || '',
      observations: this.state.observations,
      lineDistance: this.state.lineDistance || '',
      channel1Type: this.state.channel1Type,
      channel2Type: this.state.channel2Type,
      channel3Type: this.state.channel3Type,
      records: this.state.records
    });
  };

  handleProcessCompleted = (result: PrescriptionResult) => {
    this.setState({ activeStep: 3, result });
  };

  handleProcessError = (layers: RasterDocument[] | null, error: any) => {
    console.error(error);
    alert(error);
  };

  renderLoading(message?: string | null) {
    return (
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={true}
        message={
          <>
            <CircularProgress color="secondary" size={25} />
            <span>{message || 'cargando'}</span>
          </>
        }
      />
    );
  }

  renderBlankSlate(renders: Renders) {
    const { classes } = this.props;
    return (
      <Paper square={true} elevation={1} className={classes.contentCentered}>
        <Typography variant="title" color="textSecondary">
          No hay capas disponibles para realizar el análisis.
        </Typography>
      </Paper>
    );
  }

  renderSupplyGrid(renders: Renders) {
    const { classes } = this.props;
    const { zonesDoc } = this.state;

    if (renders.isBlankSlate) {
      return this.renderBlankSlate(renders);
    }

    return (
      <Paper square={true} elevation={1} className={classes.content}>
        <DocumentGrid
          selectable={true}
          documents={renders.layers}
          loading={false}
          selectedIDs={zonesDoc ? [zonesDoc.id] : []}
          onSelect={(doc: ZoneAnalysisDocument) => {
            this.setState({ zonesDoc: doc });
          }}
        />
      </Paper>
    );
  }

  renderBigLoading() {
    const { classes } = this.props;
    return (
      <Paper square={true} elevation={1} className={classes.contentCentered}>
        <CircularProgress size={150} />
        <Typography variant="title" color="textSecondary">
          Ejecutando Agro-ritmo
        </Typography>
      </Paper>
    );
  }

  renderBigThumbsUp() {
    const { classes } = this.props;
    return (
      <Paper square={true} elevation={1} className={classes.contentCentered}>
        <span style={{ fontSize: 150 }}>👍</span>
        <Typography variant="title" color="textSecondary">
          Envío Exitoso
        </Typography>
      </Paper>
    );
  }

  renderTableView() {
    const { records } = this.state;
    const { classes } = this.props;

    if (!records) {
      return;
    }

    return (
      <Paper square={true} elevation={0} className={classes.content}>
        <Card elevation={1} className={classes.card}>
          <CardHeader title="Maquinaria" />
          <CardContent className={classes.form}>
            <TextField
              label="Modelo Monitor"
              className={classes.textField}
              select={true}
              margin="normal"
              value={this.state.monitorModel}
              onChange={evt =>
                this.setState({ monitorModel: evt.target.value })
              }
            >
              <MenuItem value={'unknown'}>Desconozco</MenuItem>
              {MODELS.map(m => (
                <MenuItem value={m}>{m}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Modelo Máquina"
              className={classes.textField}
              select={true}
              margin="normal"
              value={this.state.machineryModel}
              onChange={evt =>
                this.setState({ machineryModel: evt.target.value })
              }
            >
              <MenuItem value={'unknown'}>Desconozco</MenuItem>
              {MODELS.map(m => (
                <MenuItem value={m}>{m}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Distancia entre Líneas"
              className={classes.textField}
              select={true}
              margin="normal"
              value={this.state.lineDistance}
              onChange={evt =>
                this.setState({ lineDistance: evt.target.value })
              }
            >
              <MenuItem value={'unknown'}>Desconozco</MenuItem>
              {DISTANCES.map(m => (
                <MenuItem value={m}>{m}</MenuItem>
              ))}
            </TextField>
          </CardContent>
          <CardContent>
            <TextField
              fullWidth={true}
              placeholder="Observaciones"
              value={this.state.observations}
              onChange={evt =>
                this.setState({ observations: evt.target.value })
              }
            />
          </CardContent>
        </Card>
        <Card elevation={1} className={classes.card}>
          <CardHeader title="Canales" />
          <CardContent className={classes.form}>
            <TextField
              label="Canal 1"
              className={classes.textField}
              select={true}
              margin="normal"
              value={this.state.channel1Type}
              onChange={evt =>
                this.setState({ channel1Type: evt.target.value })
              }
            >
              <MenuItem value={''}>Desactivado</MenuItem>
              {CHANNELS.map(c => (
                <MenuItem value={c}>{c}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Canal 2"
              className={classes.textField}
              select={true}
              margin="normal"
              value={this.state.channel2Type}
              onChange={evt =>
                this.setState({ channel2Type: evt.target.value })
              }
            >
              <MenuItem value={''}>Desactivado</MenuItem>
              {CHANNELS.map(c => (
                <MenuItem value={c}>{c}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Canal 3"
              className={classes.textField}
              select={true}
              margin="normal"
              value={this.state.channel3Type}
              onChange={evt =>
                this.setState({ channel3Type: evt.target.value })
              }
            >
              <MenuItem value={undefined}>Desactivado</MenuItem>
              {CHANNELS.map(c => (
                <MenuItem value={c}>{c}</MenuItem>
              ))}
            </TextField>
          </CardContent>
        </Card>
        <Card elevation={1} className={classes.card}>
          <CardHeader title="Valores x Zona" />
          <CardContent>
            <Table>
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell>Zona</TableCell>
                  <TableCell className={classes.inputCell}>
                    {this.state.channel1Type || 'Canal 1'}
                  </TableCell>
                  <TableCell className={classes.inputCell}>
                    {this.state.channel2Type || 'Canal 2'}
                  </TableCell>
                  <TableCell className={classes.inputCell}>
                    {this.state.channel3Type || 'Canal 3'}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {records.map(record => {
                  return (
                    <TableRow key={record.zoneId} hover={true}>
                      <TableCell>Zona {record.zoneId}</TableCell>
                      <TableCell className={classes.inputCell}>
                        <Input
                          placeholder="valor canal 1"
                          fullWidth={true}
                          disableUnderline={true}
                          type="number"
                          value={record.channel1 ? record.channel1 : undefined}
                          onChange={evt =>
                            (record.channel1 = parseFloat(evt.target.value))
                          }
                        />
                      </TableCell>
                      <TableCell className={classes.inputCell}>
                        <Input
                          placeholder="valor canal 2"
                          fullWidth={true}
                          disableUnderline={true}
                          type="number"
                          value={record.channel2 ? record.channel2 : undefined}
                          onChange={evt =>
                            (record.channel2 = parseFloat(evt.target.value))
                          }
                        />
                      </TableCell>
                      <TableCell className={classes.inputCell}>
                        <Input
                          placeholder="valor canal 3"
                          fullWidth={true}
                          disableUnderline={true}
                          type="number"
                          value={record.channel3 ? record.channel3 : undefined}
                          onChange={evt =>
                            (record.channel3 = parseFloat(evt.target.value))
                          }
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </Paper>
    );
  }

  composeProviders(children: (r: Renders) => React.ReactNode) {
    const {
      match: {
        params: { fieldId }
      }
    } = this.props;

    return (
      <LayoutToolsProvider>
        {layout => (
          <FieldProvider
            fieldId={fieldId}
            includeFarm={true}
            includeLayers={false}
          >
            {fieldRenders => (
              <LayersProvider
                fieldId={fieldId}
                farmId={fieldRenders.farm && fieldRenders.farm.id}
                kind={['zone-analysis']}
              >
                {layersRenders => (
                  <PrescriptionProvider
                    onCompleted={this.handleProcessCompleted}
                    onError={error =>
                      this.handleProcessError(fieldRenders.layers, error)
                    }
                  >
                    {analyzeRenders =>
                      children({
                        layout,
                        loading: fieldRenders.loading || layersRenders.loading,
                        field: fieldRenders.field,
                        farm: fieldRenders.farm,
                        layers: layersRenders.layers,
                        execute: analyzeRenders.execute,
                        processing: analyzeRenders.processing,
                        isBlankSlate:
                          !fieldRenders.loading &&
                          !layersRenders.loading &&
                          layersRenders.layers != null &&
                          !layersRenders.layers.length
                      })
                    }
                  </PrescriptionProvider>
                )}
              </LayersProvider>
            )}
          </FieldProvider>
        )}
      </LayoutToolsProvider>
    );
  }

  renderStepper(renders: Renders) {
    const { classes } = this.props;
    const { zonesDoc, formErrors } = this.state;
    return (
      <Stepper
        activeStep={this.state.activeStep}
        orientation="vertical"
        className={classes.stepper}
      >
        <Step key={0}>
          <StepLabel>Seleccionar Insumos</StepLabel>
          <StepContent>
            <Typography variant="subheading" gutterBottom={true}>
              Elija de su biblioteca el raster que desea utilizar como base para
              la definición de las zonas de gestión de su lote.
            </Typography>
            <Button
              fullWidth={true}
              color="primary"
              variant="raised"
              disabled={!zonesDoc}
              onClick={() => this.handleSelectClick(renders)}
            >
              Seleccionar
            </Button>
            {!zonesDoc && (
              <Typography color="error">
                Debe seleccionar un insumo para su análisis
              </Typography>
            )}
          </StepContent>
        </Step>
        <Step key={1}>
          <StepLabel>Definir Valores</StepLabel>
          <StepContent>
            <Typography variant="subheading">
              Seleccione los parámetros que coincidan con su maquinaria y luego
              ingrese los valores que desea aplicar para cada zona.
            </Typography>
            <Button
              fullWidth={true}
              color="primary"
              variant="raised"
              disabled={!zonesDoc}
              onClick={() => this.handleExecute(renders)}
            >
              Generar Prescripción
            </Button>
            {formErrors.length
              ? formErrors.map(err => (
                  <Typography color="error">{err}</Typography>
                ))
              : null}
          </StepContent>
        </Step>
        <Step key={2}>
          <StepLabel>Enviar Información</StepLabel>
          <StepContent>
            <Typography variant="subheading">
              Estamos enviando los parametros de prescripción para generar el
              archivo final que pueda ser utilizado por su maquinaria.
            </Typography>
          </StepContent>
        </Step>
        <Step key={3}>
          <StepLabel>Envio Exitoso</StepLabel>
          <StepContent>
            <Typography variant="subheading" gutterBottom={true}>
              Los datos han sido enviados exitosamente. Le enviaremos un correo
              cuando el proceso haya terminado de generar el archivo
              correspondiente.
            </Typography>
            <Button
              fullWidth={true}
              variant="raised"
              onClick={() => this.setState({ activeStep: 0 })}
            >
              Volver a comenzar
            </Button>
          </StepContent>
        </Step>
      </Stepper>
    );
  }

  render() {
    const { classes } = this.props;

    const { activeStep, saving } = this.state;

    return this.composeProviders(renders => (
      <main className={classes.root}>
        {renders.loading && this.renderLoading('cargando lote')}
        {saving && this.renderLoading('guardando análisis')}

        <SidePanel>
          <div style={{ display: 'flex' }}>
            <div style={{ flex: 0, marginRight: 16, alignItems: 'center' }}>
              <IconButton onClick={() => this.props.history.goBack()}>
                <BackIcon />
              </IconButton>
            </div>
            <div style={{ flex: 1 }}>
              <Typography variant="subheading">Prescripción</Typography>
              <Typography variant="headline" style={{ marginBottom: 16 }}>
                {(renders.field && renders.field.name) || 'Cargando Lote...'}
              </Typography>
            </div>
          </div>
          {this.renderStepper(renders)}
        </SidePanel>

        {activeStep === 0 && this.renderSupplyGrid(renders)}
        {activeStep === 1 && this.renderTableView()}
        {activeStep === 2 && this.renderBigLoading()}
        {activeStep === 3 && this.renderBigThumbsUp()}
      </main>
    ));
  }
}

export default withStyles(styles)(withRouter(ZoneAnalysis));



// WEBPACK FOOTER //
// ./src/containers/Prescription.tsx